import { MultiLevelMenuItem } from '~/components/MultiLevelMenu/MultiLevelMenu'
import {
  ArchiveIcon,
  CopyLinkIcon,
  DeleteIcon,
  DownloadIcon,
  NewVersionIcon,
  RecoverIcon,
  SelectedIcon,
  VersionsIcon,
} from './Icons'
import { EvaluationVersionSummary } from '~/gql/graphql'
import {
  ChangeEvaluationStatusDashboardMutateFn,
  ChangeEvaluationStatusReportMutateFn,
  CreateEvaluationVersionMutateFn,
  EvaluationStatusType,
  StatusValue,
  getCurrentEvaluationStatus,
} from './mutations'
import { useCallback, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'react-hot-toast'
import { useConfirmationModal } from '~/components/ConfirmationModal/state'
import { useRequestRecover } from '~/store/evaluation-store'
import { useShowEditEvaluationNameModal } from '../EditEvaluationNameModal/state'
import { EditEvaluationIcon } from '../EditEvaluationNameModal/icons'
import { useDashboardWorkspaceContext } from '~/app/(dashboard)/dashboardContext'
import { ClientSetupMenuItem } from '~/app/(dashboard)/clientSetupQuery'
import { useTranslation } from '~/i18n/useTranslation'
import { getTranslation, getMenuCaptionKey } from '~/i18n/translations'

export function useCreateRenameEvaluationMenuItem() {
  const showEditEvaluationNameModal = useShowEditEvaluationNameModal()
  const { t } = useTranslation()

  const createRenameEvaluationMenuItem = useCallback(
    ({
      evaluationId,
      evaluationVersion,
      evaluationName,
      hidden,
    }: {
      evaluationId: string
      evaluationVersion: number
      evaluationName: string
      hidden?: boolean
    }): MultiLevelMenuItem => {
      return {
        name: 'rename',
        label: t('eval.rename'),
        onClick: () => {
          if (!evaluationId || !evaluationVersion) {
            return
          }
          showEditEvaluationNameModal({
            evaluationId,
            evaluationVersion,
            evaluationName,
          })
        },
        IconLeft: EditEvaluationIcon,
        hidden,
      }
    },
    [t, showEditEvaluationNameModal]
  )
  return createRenameEvaluationMenuItem
}

export function createCopyEvaluationLinkMenuItem({
  url,
  t,
}: {
  url: string
  t?: (key: string) => string
}): MultiLevelMenuItem {
  return {
    name: 'copy',
    label: window.isSecureContext ? (t?.('eval.copyLink') ?? 'Copy link') : 'Copy (insecure connection)',
    onClick() {
      navigator.clipboard.writeText(url)
      toast.success(t?.('toast.linkCopied') ?? 'Link copied.')
    },
    IconLeft: CopyLinkIcon,
    disabled: !window.isSecureContext,
  }
}

export function createDownloadEvaluationReportMenuItem({
  onClick,
  hidden,
  disabled,
  t,
}: {
  onClick: () => void
  hidden?: boolean
  disabled?: boolean
  t?: (key: string) => string
}): MultiLevelMenuItem {
  return {
    name: 'downloadReport',
    label: t?.('eval.downloadReport') ?? 'Download report',
    onClick,
    IconLeft: DownloadIcon,
    hidden,
    disabled,
  }
}

export function createDownloadEvaluationTablesMenuItem({
  onClick,
  hidden,
  disabled,
  t,
}: {
  onClick: () => void
  hidden?: boolean
  disabled?: boolean
  t?: (key: string) => string
}): MultiLevelMenuItem {
  return {
    name: 'downloadTables',
    label: t?.('eval.downloadTables') ?? 'Download tables',
    onClick,
    IconLeft: DownloadIcon,
    hidden,
    disabled,
  }
}

export function useCreateClientSetupEvaluationMenuItems({
  onClickDownloadTables,
  onClickDownloadScoreDetail,
}: {
  onClickDownloadTables?: () => void
  onClickDownloadScoreDetail?: () => void
}): MultiLevelMenuItem[] {
  const { clientSetup } = useDashboardWorkspaceContext()
  const { locale } = useTranslation()

  // Map of icons for each icon string.
  const iconMap = useMemo(() => {
    return {
      download: DownloadIcon,
      ['download-tables']: DownloadIcon,
      ['download-score-detail']: DownloadIcon,
    }
  }, [])

  // Action map
  const actionMap = useMemo(() => {
    return {
      ['download-tables']: onClickDownloadTables,
      ['download-score-detail']: onClickDownloadScoreDetail,
    }
  }, [onClickDownloadTables, onClickDownloadScoreDetail])

  // Recursive function to create menu items for each item in the client setup menus.
  const createMenuItem = useCallback(
    (item: ClientSetupMenuItem): MultiLevelMenuItem => {
      const hasSubMenuItems = !!item['sub-menus']?.length
      const action =
        actionMap[item.key] ?? (() => window.open(item.url, '_blank'))
      const tKey = getMenuCaptionKey(item.caption)
      const menuItem: MultiLevelMenuItem = {
        name: item.key,
        label: tKey ? getTranslation(locale, tKey) : item.caption,
        onClick: action && !hasSubMenuItems ? action : undefined,
        IconLeft: iconMap[item.icon],
        subMenu: hasSubMenuItems
          ? item['sub-menus']?.map(createMenuItem)
          : undefined,
      }
      return menuItem
    },
    [iconMap, actionMap, locale]
  )

  // Recursively create menu items for each item in the client setup menus.
  const menuItems = useMemo(() => {
    if (!clientSetup?.menus) return []

    // Parse the JSON string if it's a string
    let menusData =
      typeof clientSetup.menus === 'string'
        ? JSON.parse(clientSetup.menus)
        : clientSetup.menus

    if (!menusData.evaluation || typeof menusData.evaluation !== 'object')
      return []

    // Seeing instances where the menusData.evaluation is not an array,
    // and directly a menu item object.
    // so we need to handle that.
    if (!Array.isArray(menusData?.evaluation)) {
      if (menusData.evaluation.key && menusData.evaluation.caption) {
        menusData.evaluation = [menusData.evaluation]
      } else {
        menusData.evaluation = []
      }
    }
    return menusData?.evaluation?.map(createMenuItem) ?? []
  }, [clientSetup?.menus, createMenuItem])

  return menuItems
}

export function useCreateArchiveEvaluationMenuItem({
  mutate,
}: {
  mutate:
    | ChangeEvaluationStatusReportMutateFn
    | ChangeEvaluationStatusDashboardMutateFn
}) {
  const { t } = useTranslation()
  const createArchiveEvaluationMenuItem = useCallback(
    ({
      evaluationId,
      evaluationVersionId,
      hidden,
      status,
      isLoading,
    }: {
      evaluationId: string
      evaluationVersionId: string
      hidden?: boolean
      status: Array<{ key: string; value: string }>
      isLoading?: boolean
    }): MultiLevelMenuItem => {
      const statusValue =
        (getCurrentEvaluationStatus(status).value as StatusValue) ??
        StatusValue.active

      const label =
        statusValue === StatusValue.archived ? t('eval.unarchive') : t('eval.archive')

      return {
        name: 'archive',
        label,
        onClick: () => {
          if (
            !evaluationId ||
            ![StatusValue.archived, StatusValue.active].includes(statusValue)
          ) {
            return
          }
          const toastId = toast.loading(
            statusValue === StatusValue.archived
              ? t('toast.unarchivingEvaluation')
              : t('toast.archivingEvaluation')
          )
          mutate({
            evaluationId,
            evaluationVersionId,
            newStatus:
              statusValue === StatusValue.active
                ? StatusValue.archived
                : StatusValue.active,
          })
            .then(() => {
              toast.success(
                statusValue === StatusValue.archived
                  ? t('toast.evaluationUnarchived')
                  : t('toast.evaluationArchived')
              )
            })
            .catch(() => {
              toast.error(t('toast.errorOccurred'))
            })
            .finally(() => {
              toast.dismiss(toastId)
            })
        },
        IconLeft: ArchiveIcon,
        loading: isLoading,
        hidden:
          hidden ||
          ![StatusValue.archived, StatusValue.active].includes(statusValue),
        disabled: isLoading,
      }
    },
    [t, mutate]
  )
  return createArchiveEvaluationMenuItem
}

export function useCreateDeleteEvaluationMenuItem({
  mutate,
}: {
  mutate:
    | ChangeEvaluationStatusReportMutateFn
    | ChangeEvaluationStatusDashboardMutateFn
}) {
  const { show } = useConfirmationModal()
  const { t } = useTranslation()
  const createDeleteEvaluationMenuItem = useCallback(
    ({
      evaluationId,
      evaluationVersionId,
      evaluationName,
      hidden,
      status,
      isLoading,
    }: {
      evaluationId: string
      evaluationVersionId: string
      evaluationName: string
      hidden?: boolean
      status: EvaluationStatusType
      isLoading?: boolean
    }): MultiLevelMenuItem => {
      const statusValue =
        getCurrentEvaluationStatus(status).value ?? StatusValue.active

      return {
        name: 'delete',
        label: t('action.delete'),
        onClick: () => {
          if (!evaluationId || statusValue === StatusValue.deleted) {
            return
          }
          show({
            title: t('confirm.deleteEvalTitle'),
            description: t('confirm.deleteEvalDescription'),
            confirmButtonCaption: t('action.delete'),
            color: 'red',
            onConfirm: () => {
              const toastId = toast.loading(t('toast.deletingEvaluation'))
              mutate({
                evaluationId,
                evaluationVersionId,
                newStatus: StatusValue.deleted,
              })
                .then(() => {
                  toast.success(t('toast.evaluationDeleted'))
                })
                .catch(() => {
                  toast.error(t('toast.errorOccurred'))
                })
                .finally(() => {
                  toast.dismiss(toastId)
                })
            },
          })
        },
        IconLeft: DeleteIcon,
        loading: isLoading,
        hidden: hidden || (statusValue === StatusValue.deleted && !isLoading),
        disabled: isLoading,
      }
    },
    [t, mutate, show]
  )
  return createDeleteEvaluationMenuItem
}

export function useCreateRecoverEvaluationMenuItem() {
  const { show } = useConfirmationModal()
  const requestRecover = useRequestRecover()
  const { t } = useTranslation()

  const createRecoverEvaluationMenuItem = useCallback(
    ({
      hidden,
    }: {
      hidden?: boolean
    }): MultiLevelMenuItem => {
      return {
        name: 'recover',
        label: t('eval.recover'),
        onClick: () => {
          show({
            title: t('confirm.recoverEvalTitle'),
            description: t('confirm.recoverEvalDescription'),
            confirmButtonCaption: t('eval.recover'),
            color: 'orange',
            onConfirm: () => {
              requestRecover()
            },
          })
        },
        IconLeft: RecoverIcon,
        hidden,
      }
    },
    [t, show, requestRecover]
  )
  return createRecoverEvaluationMenuItem
}

export type EvaluationVersionsMenuVersion = {
  id: EvaluationVersionSummary['id']
  evaluationId: EvaluationVersionSummary['evaluationId']
  versionNumber: EvaluationVersionSummary['versionNumber']
}

export function useCreateEvaluationVersionsMenuItem({
  mutate,
}: {
  mutate: CreateEvaluationVersionMutateFn
}) {
  const router = useRouter()
  const { t } = useTranslation()
  const createEvaluationVersionsMenuItem = useCallback(
    ({
      versions,
      currentVersionId,
      subMenuContainerClassName,
      createEvaluationVersionLoading,
      status,
      hidden,
      isCreateNewVersionHidden,
    }: {
      versions: EvaluationVersionsMenuVersion[]
      currentVersionId: string
      subMenuContainerClassName?: string
      createEvaluationVersionLoading?: boolean
      status: EvaluationStatusType
      hidden?: boolean
      isCreateNewVersionHidden?: boolean
    }): MultiLevelMenuItem => {
      const statusValue =
        getCurrentEvaluationStatus(status).value ?? StatusValue.active

      return {
        name: 'versions',
        label: t('eval.versions'),
        IconLeft: VersionsIcon,
        subMenuContainerClassName,
        hidden,
        subMenu: [
          ...versions.map((version) => ({
            name: `version${version.versionNumber}`,
            label: `Version ${version.versionNumber}`,
            onClick() {
              router.push(`/evaluations/${encodeURIComponent(version.id)}`)
            },
            IconRight:
              version.id === currentVersionId ? SelectedIcon : undefined,
            buttonClassName:
              version.id === currentVersionId
                ? 'text-primary-500'
                : 'text-[#A9A9A9]',
          })),
          ...(!isCreateNewVersionHidden &&
          ![StatusValue.archived, StatusValue.deleted].includes(
            statusValue as StatusValue
          )
            ? [
                {
                  name: 'new-version',
                  label: t('eval.newVersion'),
                  loading: createEvaluationVersionLoading,
                  disabled: createEvaluationVersionLoading,
                  onClick() {
                    const toastId = toast.loading(t('toast.creatingVersion'))
                    return mutate({
                      evaluationId: null,
                      evaluationVersionId: currentVersionId,
                    })
                      .then((response) => {
                        const evaluationId =
                          response?.createEvaluationVersion?.evaluation?.id
                        toast.success(t('toast.versionCreated'))
                        if (!evaluationId) {
                          return undefined
                        }
                        router.push(`/evaluations/${evaluationId}`)
                      })
                      .catch(() => {
                        toast.error(t('toast.errorOccurred'))
                      })
                      .finally(() => {
                        toast.dismiss(toastId)
                      })
                  },
                  IconRight: NewVersionIcon,
                  containerClassName: 'border-t border-gray-100',
                },
              ]
            : []),
        ],
      }
    },
    [t, mutate, router]
  )
  return createEvaluationVersionsMenuItem
}
